import type { LoginBody, LoginResult, Me, MfaCodeBody } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, del, get, hasSession, post } from './client';

export const meKey = ['me'] as const;

/** Who is signed in. null = nobody (shows the sign-in page). */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      if (!hasSession()) return null; // nobody signed in: no need to ask
      try {
        return await get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/** Step one: the password. Signed in (on a remembered device), or the code is needed next. */
export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginBody) => post<LoginResult>('/auth/login', body),
    onSuccess: (r) => {
      if (r.mfa) return;
      qc.clear();
      qc.setQueryData(meKey, r);
    },
  });
}

/** Step two: the 6-digit code from the authenticator app. */
export function useLoginCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MfaCodeBody) => post<Me>('/auth/login/code', body),
    onSuccess: (me) => {
      qc.clear();
      qc.setQueryData(meKey, me);
    },
  });
}

export interface TwoFactor {
  enabledAt: string | null;
  /** False: an admin turned two-factor off for you. */
  required: boolean;
  devices: { id: string; userAgent: string | null; createdAt: string; lastUsedAt: string | null; expiresAt: string; current: boolean }[];
}
const twoFactorKey = ['two-factor'] as const;
export const useTwoFactor = () => useQuery({ queryKey: twoFactorKey, queryFn: () => get<TwoFactor>('/auth/two-factor') });
/** Forget one remembered device, or all of them (no id). */
export function useForgetDevices() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id?: string) => del(`/auth/two-factor/devices${id ? `/${id}` : ''}`), onSuccess: () => void qc.invalidateQueries({ queryKey: twoFactorKey }) });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<void>('/auth/logout'),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(meKey, null);
    },
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => post<Me>('/auth/change-password', body),
    onSuccess: (me) => qc.setQueryData(meKey, me),
  });
}

export const useForgotPassword = () => useMutation({ mutationFn: (body: { email: string }) => post<{ message: string }>('/auth/forgot-password', body) });
export const useResetPassword = () =>
  useMutation({ mutationFn: (body: { token: string; password: string }) => post<{ message: string }>('/auth/reset-password', body) });
